import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Bike, Plus, Copy, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api';

const RiderManagement = ({ storeId }) => {
  const [riders, setRiders] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRider, setNewRider] = useState({ name: '', phone: '' });
  const [createdRider, setCreatedRider] = useState(null);
  const [showOTPModal, setShowOTPModal] = useState(false);

  useEffect(() => {
    loadRiders();
  }, [storeId]);

  const loadRiders = async () => {
    try {
      // Get all users with RIDER role for this store
      const response = await apiClient.get(`/auth/riders?store_id=${storeId}`);
      setRiders(response.data || []);
    } catch (error) {
      console.error('Error loading riders:', error);
      // If endpoint doesn't exist or no riders, show empty state
      setRiders([]);
    }
  };

  const handleCreateRider = async () => {
    if (!newRider.name || !newRider.phone) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate phone number (10 digits)
    if (!/^\d{10}$/.test(newRider.phone)) {
      toast.error('Phone number must be 10 digits');
      return;
    }

    try {
      const response = await apiClient.post('/auth/create-rider', {
        name: newRider.name,
        phone: newRider.phone
      });

      setCreatedRider(response.data);
      setShowCreateModal(false);
      setShowOTPModal(true);
      setNewRider({ name: '', phone: '' });
      
      toast.success('Rider created successfully!');
      loadRiders();
    } catch (error) {
      console.error('Error creating rider:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create rider';
      toast.error(errorMessage);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const copyCredentials = () => {
    const text = `Rider Login Details:\nPhone: ${createdRider.rider.phone}\nOTP/Password: ${createdRider.otp}\n\nLogin at: ${window.location.origin}/login`;
    navigator.clipboard.writeText(text);
    toast.success('Credentials copied! Share with rider.');
  };

  const handleResetPassword = async (rider) => {
    if (!confirm(`Reset password for ${rider.name}?\n\nA new 6-digit OTP will be generated.`)) {
      return;
    }

    try {
      const response = await apiClient.post(`/auth/reset-rider-password/${rider.id}`);
      
      // Show the new OTP
      setCreatedRider({
        rider: rider,
        otp: response.data.new_otp,
        instructions: `Share this new OTP with ${rider.name}`
      });
      setShowOTPModal(true);
      
      toast.success('Password reset successfully!');
    } catch (error) {
      console.error('Error resetting password:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to reset password';
      toast.error(errorMessage);
    }
  };

  const handleToggleActive = async (rider) => {
    const action = rider.active ? 'deactivate' : 'activate';
    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${rider.name}?`)) {
      return;
    }

    try {
      await apiClient.put(`/auth/riders/${rider.id}/toggle-active`);
      toast.success(`Rider ${action}d successfully!`);
      loadRiders();
    } catch (error) {
      console.error('Error toggling rider status:', error);
      const errorMessage = error.response?.data?.detail || `Failed to ${action} rider`;
      toast.error(errorMessage);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Rider Management</h2>
        <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Create Rider
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {riders.map((rider) => (
          <Card key={rider.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center">
                  <Bike className="w-5 h-5 mr-2 text-blue-600" />
                  {rider.name}
                </div>
                <div className={`px-2 py-1 rounded text-xs font-bold ${rider.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {rider.active ? 'Active' : 'Inactive'}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium font-mono">{rider.phone}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium text-xs">{rider.email}</span>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                  <p className="text-xs text-yellow-800 font-semibold mb-1">Login Credentials:</p>
                  <p className="text-xs text-yellow-700">
                    Use phone number as username. Password was auto-generated during creation.
                  </p>
                  <p className="text-xs text-yellow-600 mt-1">
                    <strong>Note:</strong> OTP is shown only once during creation for security.
                  </p>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleResetPassword(rider)}
                    className="flex-1"
                  >
                    Reset Password
                  </Button>
                  <Button 
                    size="sm" 
                    variant={rider.active ? "destructive" : "default"}
                    onClick={() => handleToggleActive(rider)}
                    className="flex-1"
                  >
                    {rider.active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {riders.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <Bike className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No riders yet. Create your first rider!</p>
          </div>
        )}
      </div>

      {/* Create Rider Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Rider</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rider Name *</Label>
              <Input
                value={newRider.name}
                onChange={(e) => setNewRider({ ...newRider, name: e.target.value })}
                placeholder="Enter rider's full name"
              />
            </div>
            <div>
              <Label>Phone Number * (10 digits)</Label>
              <Input
                type="tel"
                maxLength="10"
                value={newRider.phone}
                onChange={(e) => setNewRider({ ...newRider, phone: e.target.value.replace(/\D/g, '') })}
                placeholder="9876543210"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> A 6-digit OTP will be auto-generated as the initial password. 
                You'll need to share this OTP with the rider.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateRider} className="bg-blue-600 hover:bg-blue-700">
              Create Rider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OTP Display Modal */}
      <Dialog open={showOTPModal} onOpenChange={setShowOTPModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-green-600">
              <CheckCircle className="w-6 h-6 mr-2" />
              Rider Created Successfully!
            </DialogTitle>
          </DialogHeader>
          {createdRider && (
            <div className="space-y-4">
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                <h3 className="font-bold text-lg mb-3">Share These Details with Rider:</h3>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-gray-600">Rider Name</Label>
                    <div className="flex items-center justify-between bg-white p-2 rounded border mt-1">
                      <span className="font-medium">{createdRider.rider.name}</span>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-gray-600">Phone (Login Username)</Label>
                    <div className="flex items-center justify-between bg-white p-2 rounded border mt-1">
                      <span className="font-mono font-bold text-lg">{createdRider.rider.phone}</span>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdRider.rider.phone)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-gray-600">OTP / Password</Label>
                    <div className="flex items-center justify-between bg-yellow-100 p-3 rounded border-2 border-yellow-400 mt-1">
                      <span className="font-mono font-bold text-2xl text-yellow-900">{createdRider.otp}</span>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdRider.otp)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Instructions:</strong>
                </p>
                <ol className="text-sm text-blue-800 mt-2 ml-4 list-decimal space-y-1">
                  <li>Rider opens the SSPICE app</li>
                  <li>Login with phone: <strong>{createdRider.rider.phone}</strong></li>
                  <li>Enter OTP: <strong>{createdRider.otp}</strong></li>
                  <li>Click "Go Online" in Rider Dashboard</li>
                </ol>
              </div>
              
              <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={copyCredentials}>
                <Copy className="w-4 h-4 mr-2" />
                Copy All Details to Share
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowOTPModal(false)} className="w-full">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RiderManagement;
